import React, { useCallback, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { useBlocker } from 'react-router-dom'
import { Box, FieldDropdown, SpinnerLoader, Tabs, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { RcsEmptyFallbackInboxAlert, RcsNotSupportedFilesAlert, SendFromRcs } from 'entities/Rcs/ui'
import { useTriggerViewContext } from 'widgets/TriggerView/context/triggerViewContext'
import { TriggerName } from 'widgets/TriggerView/ui/TriggerName/TriggerName'
import { TriggerViewPreview } from 'widgets/TriggerView/ui/TriggerViewPreview/TriggerViewPreview'
import { TriggerSMS } from 'widgets/TriggerView/ui/TriggerSMS/TriggerSMS'
import { IntegrationDropdown } from 'widgets/IntegrationDropdown'
import { AdvancedTrackConversions } from 'widgets/AdvancedTrackConversions/AdvancedTrackConversions'
import { TriggerActions } from 'widgets/TriggerView/ui/TriggerActions/TriggerActions'
import { TagsDropdown } from 'widgets/TagsDropdown'
import { RinglessMessageField } from 'widgets/MessageFormFields/RinglessMessageField'
import { SendFrom } from 'widgets/SendFrom'
import styles from './styles.module.scss'

export const TriggerViewContent = observer(() => {
  const { subscriptionStore } = useEntitiesContext()
  const { isPAYGPlan } = subscriptionStore
  const {
    loading,
    mode,
    type,
    tabs,
    selectTypeTab,
    ringlessMessageFieldStore,
    integrationDropdownStore,
    handleShowKeepEditingModal,
    triggerSendFromStore,
    errorsMap,
    setError,
    trackConversionStore,
    tagsControlStore,
    isOpenAdvanced,
    isExistChanges,
    setOpenAdvanced,
    reset,
    rcsInbox,
    triggerSMSStore: {
      messageFieldStore: { showRcsNotSupportedFilesAlert, setShowRcsNotSupportedFilesAlert },
    },
  } = useTriggerViewContext()
  const blocker = useBlocker(() => isExistChanges)
  const beforeUnloadHandler = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault()
    event.returnValue = true
  }, [])

  useEffect(() => {
    return () => {
      reset()
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [])

  useEffect(() => {
    uiStore.setRoutesBlocker(blocker)
    handleShowKeepEditingModal(blocker)
  }, [blocker])

  useEffect(() => {
    uiStore.setRoutesBlocker(isExistChanges ? blocker : null)

    if (isExistChanges) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [isExistChanges])

  if (loading) {
    return (
      <div className={styles.loading}>
        <SpinnerLoader ariaLabel={'TriggerViewContent'} />
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <div className={classnames(styles.box, styles.box__main)}>
        <Typography variant={'body-xs-regular'} className={styles.label} ariaLabel={'NewTrigger'}>
          {`${mode === 'edit' ? 'Edit' : 'New'} trigger`}
        </Typography>
        <TriggerName />
        <div className={styles.tabs}>
          <Tabs
            variant={'simple'}
            tabs={tabs}
            activeTabKey={type}
            handleSelectTab={selectTypeTab}
          />
          {showRcsNotSupportedFilesAlert && type === 'rcs' && (
            <RcsNotSupportedFilesAlert
              alertProps={{ marginTop: 8, marginBottom: 4 }}
              onClose={() => setShowRcsNotSupportedFilesAlert(false)}
            />
          )}
        </div>
        {(type === 'sms' || type === 'rcs') && <TriggerSMS />}
        {type === 'ringless' && (
          <RinglessMessageField store={ringlessMessageFieldStore} dropdownWidth={465} />
        )}
        {type === 'rcs' && <RcsEmptyFallbackInboxAlert alertProps={{ marginTop: 32 }} />}
        <div className={styles.section}>
          <div className={styles.section__box}>
            <IntegrationDropdown store={integrationDropdownStore} />
          </div>
          <div className={styles.section__box}>
            {(type === 'sms' || type === 'ringless') && (
              <FieldDropdown
                labelText={'Send from'}
                dropdownComponent={
                  <SendFrom
                    store={triggerSendFromStore.sendFromStore}
                    triggerVariant={'textField'}
                    textFiledTriggerProps={{
                      error: {
                        message: errorsMap.get('sendFrom'),
                      },
                    }}
                    onClearError={() => setError('sendFrom', '')}
                  />
                }
              />
            )}
            {type === 'rcs' && rcsInbox && <SendFromRcs inbox={rcsInbox} />}
          </div>
          <div className={styles.section__box}>
            <TagsDropdown controlStore={tagsControlStore} labelText='Tags' />
          </div>

          {(type === 'sms' || type === 'rcs') && !isPAYGPlan && (
            <>
              <Box height={26} />
              <AdvancedTrackConversions
                trackConversionStore={trackConversionStore}
                isOpenAdvanced={isOpenAdvanced}
                setOpenAdvanced={setOpenAdvanced}
              />
            </>
          )}

          <TriggerActions />
        </div>
      </div>

      <div className={classnames(styles.box, styles.box__preview)}>
        <TriggerViewPreview />
      </div>
    </div>
  )
})
